import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import type { TooltipContentProps } from 'recharts'
import type { DailyStatItem } from '../api/admin-api'

interface DailyStatsChartProps {
  data: DailyStatItem[]
  onBarClick?: (date: string) => void
}

function normalizeDateInput(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/)
    return match?.[1] ?? trimmed
  }

  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value.toISOString().slice(0, 10)
  }

  return null
}

function extractDateFromBarClick(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null

  const datum = data as {
    date?: unknown
    payload?: {
      date?: unknown
    }
  }

  return normalizeDateInput(datum.date) ?? normalizeDateInput(datum.payload?.date)
}

function formatDateLabel(dateStr: string): string {
  const date = new Date(dateStr)
  const dayNames = ['일', '월', '화', '수', '목', '금', '토']
  return `${date.getMonth() + 1}/${date.getDate()} (${dayNames[date.getDay()]})`
}

function formatNumber(num: number): string {
  return num.toLocaleString('ko-KR')
}

function CustomTooltip({ active, payload, label }: Partial<TooltipContentProps<number, string>>) {
  if (!active || !payload?.length) return null
  const rawValue = payload[0]?.value
  const videoCount = typeof rawValue === 'number' ? rawValue : Number(rawValue)
  if (!Number.isFinite(videoCount)) return null

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="text-sm font-medium text-gray-900 mb-2">
        {formatDateLabel(String(label))}
      </p>
      <div className="space-y-1">
        <p className="text-sm text-emerald-600">
          비디오: <span className="font-semibold">{formatNumber(videoCount)}</span>건
        </p>
      </div>
    </div>
  )
}

export function DailyStatsChart({ data, onBarClick }: DailyStatsChartProps) {
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={(value) => {
            const d = new Date(value)
            return `${d.getMonth() + 1}/${d.getDate()}`
          }}
          tick={{ fontSize: 11, fill: '#64748B' }}
          axisLine={{ stroke: '#E2E8F0' }}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#64748B' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(value) => formatNumber(value)}
          width={45}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F1F5F9' }} />
        <Bar
          dataKey="videoCount"
          fill="#10B981"
          radius={[4, 4, 0, 0]}
          maxBarSize={32}
          cursor={onBarClick ? 'pointer' : undefined}
          onClick={(entry) => {
            if (!onBarClick) return
            const date = extractDateFromBarClick(entry)
            if (date) onBarClick(date)
          }}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}
